import { apply } from './apply';
/**
 * Rebuilds a document by replaying entries over a checkpoint.
 *
 * Entries at or before the checkpoint's sequence are ignored: they are already folded in, and
 * applying them twice would duplicate their effects.
 */
export function replay(checkpoint, entries) {
    let document = checkpoint.document;
    let applied = 0;
    const skipped = [];
    const ordered = [...entries]
        .filter((entry) => entry.sequence > checkpoint.sequence)
        .sort((a, b) => a.sequence - b.sequence);
    const seen = new Set();
    for (const entry of ordered) {
        // Idempotency. A command can appear twice after a crash between writing the journal and
        // acknowledging it; applying it twice would duplicate the edit.
        if (seen.has(entry.command.meta.operationId)) {
            skipped.push({ sequence: entry.sequence, reason: 'duplicate operation' });
            continue;
        }
        seen.add(entry.command.meta.operationId);
        const result = apply(document, entry.command);
        if (!result.ok) {
            skipped.push({ sequence: entry.sequence, reason: result.error.code });
            continue;
        }
        document = result.value.document;
        applied += 1;
    }
    return { document, applied, skipped };
}
/**
 * Serialises a command for storage or the wire.
 *
 * `structuredClone` rather than `JSON.parse(JSON.stringify(...))`: it preserves the value
 * faithfully and throws on anything that cannot cross a boundary, so a command carrying
 * something unserialisable fails here rather than silently losing a field on the way to disk.
 */
export function serialize(cmd) {
    return structuredClone(cmd);
}
/**
 * The reverse. Deliberately does no validation.
 *
 * Journal entries are validated by `@orbit-mind/validators` at the storage boundary, where the
 * failure can be reported with its position. Re-checking here would duplicate the rule and
 * disagree with it the first time one of the two changed.
 */
export function deserialize(value) {
    return value;
}
/** Entries still waiting for the server, oldest first — the sync outbox. */
export function pending(entries) {
    return entries.filter((entry) => !entry.synced).sort((a, b) => a.sequence - b.sequence);
}
/**
 * Whether a checkpoint is due.
 *
 * Both conditions matter: a burst of edits should not leave an unbounded tail to replay, and a
 * session that goes quiet should still be cheap to recover rather than staying one keystroke
 * behind its last checkpoint forever.
 */
export function shouldCheckpoint(input) {
    const everyEntries = input.everyEntries ?? 50;
    const everyMs = input.everyMs ?? 5 * 60_000;
    if (input.entriesSinceCheckpoint === 0)
        return false;
    return input.entriesSinceCheckpoint >= everyEntries || input.msSinceLastCheckpoint >= everyMs;
}
//# sourceMappingURL=journal.js.map